import axios from "axios";

export const DOWNLOAD_STATUS = {
  QUEUED: "queued",
  PREPARING: "preparing",
  DOWNLOADING: "downloading",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
};

export const ACTIVE_DOWNLOAD_STATUSES = new Set([
  DOWNLOAD_STATUS.QUEUED,
  DOWNLOAD_STATUS.PREPARING,
  DOWNLOAD_STATUS.DOWNLOADING,
]);

const MB = 1024 * 1024;

const readNumberEnv = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export const DOWNLOAD_CONCURRENCY = readNumberEnv(
  process.env.REACT_APP_DOWNLOAD_CONCURRENCY,
  1,
);

// Files up to this size are fetched in-page so real byte progress can be
// shown; the whole file is held in memory until it is saved. Larger or
// unknown-size files are handed to the browser's own download manager.
export const IN_PAGE_DOWNLOAD_LIMIT_BYTES =
  readNumberEnv(process.env.REACT_APP_DOWNLOAD_MEMORY_LIMIT_MB, 512) * MB;

const backendUrl = () => process.env.REACT_APP_BACKEND_URL || "";

export class DownloadRequestError extends Error {
  constructor(message, { code, status, retryable = true, authExpired = false, requestId } = {}) {
    super(message);
    this.name = "DownloadRequestError";
    this.code = code || "DOWNLOAD_FAILED";
    this.status = status ?? null;
    this.retryable = retryable;
    this.authExpired = authExpired;
    this.requestId = requestId || null;
  }
}

const NON_RETRYABLE_CODES = new Set([
  "INVALID_VIDEO_ID",
  "VIDEO_NOT_FOUND",
  "NO_DOWNLOADABLE_FILE",
  "SUBSCRIPTION_REQUIRED",
  "DOWNLOAD_QUOTA_EXCEEDED",
  "SESSION_REVOKED",
]);

const FALLBACK_MESSAGES = {
  INVALID_VIDEO_ID: "This video link is invalid.",
  VIDEO_NOT_FOUND: "This video could not be found. It may have been removed.",
  NO_DOWNLOADABLE_FILE: "This video doesn't have a downloadable file yet.",
  SOURCE_UNAVAILABLE: "The files for this video are currently unavailable.",
  STORAGE_UNAVAILABLE: "Video storage is temporarily unavailable. Please try again.",
  STORAGE_TIMEOUT: "Our storage took too long to respond. Please try again.",
  DOWNLOAD_TICKET_EXPIRED: "This download link expired. Please retry.",
  DOWNLOAD_TICKET_INVALID: "This download link is not valid. Please retry.",
  SUBSCRIPTION_REQUIRED: "An active subscription is required to download this video.",
  DOWNLOAD_QUOTA_EXCEEDED: "You've reached your daily download limit.",
  SESSION_REVOKED: "You were signed out on this device. Please sign in again.",
};

export const cancelledError = () =>
  new DownloadRequestError("Download cancelled.", { code: "CANCELLED", retryable: true });

export const networkError = () =>
  new DownloadRequestError(
    "Network connection lost. Check your connection and try again.",
    { code: "NETWORK_ERROR", retryable: true },
  );

export const interruptedError = () =>
  new DownloadRequestError(
    "The download was interrupted before it finished. Please retry.",
    { code: "TRANSFER_INTERRUPTED", retryable: true },
  );

/** Map an HTTP failure from the download API to a user-facing error. */
export function toDownloadError(status, data) {
  const body = data && typeof data === "object" ? data : {};
  const code = typeof body.error === "string" ? body.error : null;
  const requestId = body.requestId;

  // verifyJWT answers bare 401/403 when the access token is missing or expired.
  if (!code && (status === 401 || status === 403)) {
    return new DownloadRequestError("Your session has expired. Please sign in again.", {
      code: "AUTH_EXPIRED",
      status,
      authExpired: true,
      requestId,
    });
  }

  const serverMessage = typeof body.message === "string" && body.message.trim()
    ? body.message.trim()
    : null;
  const message =
    serverMessage ||
    (code && FALLBACK_MESSAGES[code]) ||
    (status >= 500
      ? "Something went wrong on our side. Please try again."
      : "Download failed. Please try again.");

  return new DownloadRequestError(message, {
    code: code || (status >= 500 ? "SERVER_ERROR" : "DOWNLOAD_FAILED"),
    status,
    retryable: !NON_RETRYABLE_CODES.has(code),
    requestId,
  });
}

export async function prepareDownload({ videoId, token, signal }) {
  try {
    const response = await axios.post(
      `${backendUrl()}/user/downloadbest/${encodeURIComponent(videoId)}/prepare`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    if (!response.data?.success || !response.data?.ticket) {
      throw toDownloadError(response.status, response.data);
    }
    return response.data;
  } catch (err) {
    if (err instanceof DownloadRequestError) throw err;
    if (signal?.aborted || axios.isCancel?.(err)) throw cancelledError();
    if (err?.response) throw toDownloadError(err.response.status, err.response.data);
    throw networkError();
  }
}

export async function refreshAccessToken() {
  const response = await axios.get(`${backendUrl()}/refresh`, { withCredentials: true });
  const token = response.data?.accessToken;
  if (!token) throw new Error("No access token");
  return token;
}

export const buildStreamUrl = (videoId, ticket) =>
  `${backendUrl()}/user/downloadbest/${encodeURIComponent(videoId)}?ticket=${encodeURIComponent(ticket)}`;

export function canStreamInPage(sizeBytes, limitBytes = IN_PAGE_DOWNLOAD_LIMIT_BYTES) {
  return (
    typeof window !== "undefined" &&
    typeof window.fetch === "function" &&
    typeof window.ReadableStream !== "undefined" &&
    Number.isFinite(sizeBytes) &&
    sizeBytes > 0 &&
    sizeBytes <= limitBytes
  );
}

export function parseFileName(contentDisposition) {
  if (!contentDisposition) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Fetch the file with real byte progress. Rejects on HTTP errors, aborts and
 * short reads; a resolved promise means every advertised byte arrived.
 */
export async function streamDownload({ url, signal, onProgress, expectedBytes = null }) {
  let response;
  try {
    response = await window.fetch(url, { signal, credentials: "omit" });
  } catch (err) {
    if (signal?.aborted) throw cancelledError();
    throw networkError();
  }

  if (!response.ok) {
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    throw toDownloadError(response.status, data);
  }

  const headerTotal = Number(response.headers.get("content-length"));
  const total = Number.isFinite(headerTotal) && headerTotal > 0 ? headerTotal : expectedBytes;
  const meta = {
    quality: response.headers.get("x-download-quality"),
    fallbackReason: response.headers.get("x-download-fallback-reason"),
    fileName: parseFileName(response.headers.get("content-disposition")),
    contentType: response.headers.get("content-type") || "video/mp4",
  };

  const reader = response.body?.getReader?.();
  if (!reader) {
    throw new DownloadRequestError(
      "This browser can't track download progress.",
      { code: "STREAM_UNSUPPORTED", retryable: false },
    );
  }

  const chunks = [];
  let received = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      onProgress?.({ received, total: total || null });
    }
  } catch (err) {
    if (signal?.aborted) throw cancelledError();
    throw interruptedError();
  } finally {
    reader.releaseLock?.();
  }

  if (signal?.aborted) throw cancelledError();
  if (total && received < total) throw interruptedError();

  return {
    ...meta,
    received,
    total: total || received,
    blob: new Blob(chunks, { type: meta.contentType }),
  };
}

export function saveBlob(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "shotkut-video.mp4";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can abort the save in some browsers.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
}

// A hidden iframe (not a link) so an error response can't navigate the page.
export function triggerBrowserDownload(url) {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = url;
  document.body.appendChild(iframe);
  setTimeout(() => iframe.remove(), 5 * 60 * 1000);
}
